import { X } from 'lucide-react';
import { ProviderInfo } from '../types/provider';
import { AgentType, AgentModelOverride } from '../types/models';
import { EmbeddingIndexInfo } from '../types/embedding';

interface CustomConfig {
  id: string;
  name: string;
  config: Record<AgentType, AgentModelOverride | undefined>;
  maxOpinions?: number;
  maxPassagesPerDocument?: number;
  embeddingIndex?: string;
}

interface TemplatePickerProps {
  isOpen: boolean;
  providers: ProviderInfo[];
  customConfigs: CustomConfig[];
  embeddingIndexes: EmbeddingIndexInfo[];
  onSelect: (template: TemplateData) => void;
  onClose: () => void;
}

export interface TemplateData {
  type: 'provider' | 'custom';
  id: string;
  name: string;
  embeddingIndex?: string;
  maxOpinions?: number;
  maxPassagesPerDocument?: number;
  useReducer?: boolean;
  agents: {
    orchestrator?: AgentModelOverride;
    researcher?: AgentModelOverride;
    reducer?: AgentModelOverride;
    writer?: AgentModelOverride;
    summarizer?: AgentModelOverride;
  };
}

// const CUSTOM_CONFIGS_KEY = 'custom-model-configs';

export const TemplatePicker = ({
  isOpen,
  providers,
  customConfigs,
  embeddingIndexes,
  onSelect,
  onClose
}: TemplatePickerProps) => {
  if (!isOpen) return null;

  const handleSelectProvider = (provider: ProviderInfo) => {
    // Convert regular provider to template data
    // Note: Regular providers only have model names, not full configs
    // We'll need to populate with defaults
    const template: TemplateData = {
      type: 'provider',
      id: provider.id,
      name: provider.displayName,
      embeddingIndex: embeddingIndexes.find(idx => idx.isDefault)?.indexName,
      maxOpinions: provider.maxOpinions || 5,
      maxPassagesPerDocument: 3,
      useReducer: provider.useReducer,
      agents: {
        orchestrator: provider.agentModels.orchestrator ? {
          providerType: provider.providerType,
          modelId: provider.agentModels.orchestrator,
          temperature: undefined,
          topP: undefined,
          seed: undefined,
          frequencyPenalty: undefined,
          presencePenalty: undefined,
          systemPromptHash: undefined
        } : undefined,
        researcher: provider.agentModels.researcher ? {
          providerType: provider.providerType,
          modelId: provider.agentModels.researcher,
          temperature: undefined,
          topP: undefined,
          seed: undefined,
          frequencyPenalty: undefined,
          presencePenalty: undefined,
          systemPromptHash: undefined
        } : undefined,
        reducer: provider.agentModels.reducer ? {
          providerType: provider.providerType,
          modelId: provider.agentModels.reducer,
          temperature: undefined,
          topP: undefined,
          seed: undefined,
          frequencyPenalty: undefined,
          presencePenalty: undefined,
          systemPromptHash: undefined
        } : undefined,
        writer: provider.agentModels.writer ? {
          providerType: provider.providerType,
          modelId: provider.agentModels.writer,
          temperature: undefined,
          topP: undefined,
          seed: undefined,
          frequencyPenalty: undefined,
          presencePenalty: undefined,
          systemPromptHash: undefined
        } : undefined,
        summarizer: provider.agentModels.summarizer ? {
          providerType: provider.providerType,
          modelId: provider.agentModels.summarizer,
          temperature: undefined,
          topP: undefined,
          seed: undefined,
          frequencyPenalty: undefined,
          presencePenalty: undefined,
          systemPromptHash: undefined
        } : undefined
      }
    };
    onSelect(template);
  };

  const handleSelectCustomConfig = (config: CustomConfig) => {
    const template: TemplateData = {
      type: 'custom',
      id: config.id,
      name: config.name,
      embeddingIndex: config.embeddingIndex,
      maxOpinions: config.maxOpinions || 5,
      maxPassagesPerDocument: config.maxPassagesPerDocument || 3,
      useReducer: config.config.reducer?.useReducer || false,
      agents: {
        orchestrator: config.config.orchestrator,
        researcher: config.config.researcher,
        reducer: config.config.reducer,
        writer: config.config.writer,
        summarizer: config.config.summarizer
      }
    };
    onSelect(template);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div data-component="TemplatePicker" className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Load from Template</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Custom Configs Section */}
            {customConfigs.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Custom Configurations</h4>
                <div className="space-y-2">
                  {customConfigs.map(config => (
                    <button
                      key={config.id}
                      onClick={() => handleSelectCustomConfig(config)}
                      className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{config.name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Custom configuration with {Object.values(config.config).filter(Boolean).length} agents configured
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Regular Providers Section */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Regular Providers</h4>
              <div className="space-y-2">
                {providers.filter(p => p.isEnabled).map(provider => (
                  <button
                    key={provider.id}
                    onClick={() => handleSelectProvider(provider)}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{provider.displayName}</span>
                      {provider.isDefault && (
                        <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {provider.providerType} • {Object.keys(provider.agentModels).length} agents
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
